/**
 * useChatMessages Hook
 * ====================
 * 
 * Handles message fetching, sending, and state management.
 * 
 * Preserves the POST → GET → setMessages re-hydration pattern.
 * 
 * Usage:
 *   const {
 *     messages, loading, error,
 *     sendMessage, refreshMessages
 *   } = useChatMessages(matchId, token);
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import apiClient from "../api/client";


export function useChatMessages(matchId, token) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  
  // Track mounted state to prevent state updates after unmount
  const mountedRef = useRef(true);
  
  // Fetch messages
  const fetchMessages = useCallback(async () => {
    if (!matchId || !token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await apiClient.get(
        `/chat/${matchId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      );
      
      if (mountedRef.current) {
        setMessages(response.data || []);
        setError(null);
      }
    } catch (err) {
      console.error('[useChatMessages] Fetch error:', err);
      if (mountedRef.current) {
        setError(err.response?.data?.detail || 'Failed to load messages');
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [matchId, token]);
  
  // Refresh messages (public API)
  const refreshMessages = useCallback(() => {
    return fetchMessages();
  }, [fetchMessages]);
  
  // Send message - preserves POST → GET pattern
  const sendMessage = useCallback(async (text, options = {}) => {
    if (!matchId || !token || !text.trim()) {
      return { success: false, error: 'Invalid message' };
    }
    
    setSending(true);
    
    try {
      // POST the message
      const response = await apiClient.post(
        `/chat/${matchId}/messages`,
        { text: text.trim(), ...options },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        }
      );
      
      // Re-fetch messages to get server-side state (POST → GET pattern)
      await fetchMessages();
      
      return { success: true, message: response.data };
    } catch (err) {
      console.error('[useChatMessages] Send error:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Failed to send message' 
      };
    } finally {
      if (mountedRef.current) {
        setSending(false);
      }
    }
  }, [matchId, token, fetchMessages]);
  
  // Send audio message
  const sendAudioMessage = useCallback(async (audioBlob, durationMs) => {
    if (!matchId || !token || !audioBlob) {
      return { success: false, error: 'Invalid audio' };
    }
    
    setSending(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice.webm');
      formData.append('duration_ms', String(durationMs));
      
      const response = await apiClient.post(
        `/chat/${matchId}/voice`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000,
        }
      );
      
      // Re-fetch messages
      await fetchMessages();
      
      return { success: true, message: response.data };
    } catch (err) {
      console.error('[useChatMessages] Audio send error:', err);
      return { 
        success: false, 
        error: err.response?.data?.detail || 'Failed to send voice message' 
      };
    } finally {
      if (mountedRef.current) {
        setSending(false);
      }
    }
  }, [matchId, token, fetchMessages]);
  
  // Add reaction to message
  const addReaction = useCallback(async (messageId, emoji) => {
    if (!matchId || !token || !messageId) {
      return { success: false };
    }
    
    try {
      await apiClient.post(
        `/chat/${matchId}/messages/${messageId}/react`,
        { emoji },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Update local state optimistically
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const reactions = msg.reactions || [];
          return {
            ...msg,
            reactions: [...reactions, { emoji }],
          };
        }
        return msg;
      }));
      
      return { success: true };
    } catch (err) {
      console.error('[useChatMessages] Reaction error:', err);
      return { success: false };
    }
  }, [matchId, token]);
  
  // Remove reaction from message
  const removeReaction = useCallback(async (messageId, emoji) => {
    if (!matchId || !token || !messageId) {
      return { success: false };
    }
    
    try {
      await apiClient.delete(
        `/chat/${matchId}/messages/${messageId}/react`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { emoji },
        }
      );
      
      // Update local state
      setMessages(prev => prev.map(msg => {
        if (msg.id === messageId) {
          const reactions = (msg.reactions || []).filter(r => r.emoji !== emoji);
          return { ...msg, reactions };
        }
        return msg;
      }));
      
      return { success: true };
    } catch (err) {
      console.error('[useChatMessages] Remove reaction error:', err);
      return { success: false };
    }
  }, [matchId, token]);
  
  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchMessages();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchMessages]);
  
  return {
    messages,
    setMessages,
    loading,
    error,
    sending,
    sendMessage,
    sendAudioMessage,
    refreshMessages,
    addReaction,
    removeReaction,
  };
}

export default useChatMessages;
